// Print and Export pages open in a new tab, which starts with an empty Redux store. Instead of
// reloading every report there, the report page takes a snapshot of what the user is looking at
// (title, filters and the table exactly as shown: visible columns, sort order, expanded groups)
// and the new tab renders it. Snapshots live in localStorage for a few minutes only.

export type ReportViewKind = "print" | "export"

export interface ReportFilterValue {
    label: string
    value: string
}

export interface ReportSnapshot {
    title: string
    module: string
    filters: ReportFilterValue[]
    // Sanitised HTML of the report content (tables, cards)
    html: string
    createdAt: number
    // The report page also has its own full CSV export (more columns than the table shows)
    hasFullExport?: boolean
}

type FullExport = () => void | Promise<void>

// Full exports stay in the report tab; the export tab calls them through window.opener
const FULL_EXPORTS_KEY = "__ibexiqReportFullExports"

export const runFullExport = async (id: string | null): Promise<boolean> => {
    try {
        const fn = (window.opener as any)?.[FULL_EXPORTS_KEY]?.[id ?? ""] as FullExport | undefined
        if (!fn) return false
        await fn()
        return true
    } catch {
        return false
    }
}

const PREFIX = "ibexiq.report-snapshot."
const MAX_AGE_MS = 10 * 60 * 1000

// Read the label and value of every input in a filter bar. Works for text/date inputs, native
// selects and react-select (single value or the one-line multi-select summary).
export const readFilterValues = (root: HTMLElement | null): ReportFilterValue[] => {
    if (!root) return []
    const values: ReportFilterValue[] = []
    root.querySelectorAll<HTMLElement>(".input-container").forEach(container => {
        const label = container.querySelector(".input-label")?.textContent?.trim() ?? ""
        let value = ""
        const select = container.querySelector("select")
        const input = container.querySelector<HTMLInputElement>("input:not([type=hidden]):not([type=checkbox])")
        const reactSelect = container.querySelector<HTMLElement>("[class*='singleValue'], [class*='placeholder']")
        if (select) value = select.selectedOptions[0]?.textContent?.trim() ?? ""
        else if (reactSelect) value = container.getAttribute("title") || reactSelect.textContent?.trim() || ""
        else if (input) value = input.value
        if (label && value && !/^select/i.test(value)) values.push({ label, value })
    })
    return values
}

// Copy of the content without controls (buttons, inputs, resize handles, sort arrows)
const cleanHtml = (el: HTMLElement): string => {
    const copy = el.cloneNode(true) as HTMLElement
    const cellInputs = "table input:not([type=hidden]):not([type=checkbox])"
    const liveInputs = el.querySelectorAll<HTMLInputElement>(cellInputs)
    copy.querySelectorAll<HTMLInputElement>(cellInputs)
        .forEach((input, i) => input.replaceWith(liveInputs[i]?.value ?? ""))
    copy.querySelectorAll(
        ".breadcrumb-container, .search-form-container, button, input, select, textarea, script, style, iframe, object, embed, .col-resize-handle, .sort-indicator, .d-print-none, .column-visibility-dropdown"
    ).forEach(n => n.remove())
    copy.querySelectorAll("*").forEach(n => {
        Array.from(n.attributes).forEach(a => {
            if (a.name.startsWith("on") || a.value.trim().toLowerCase().startsWith("javascript:")) n.removeAttribute(a.name)
        })
    })
    return copy.innerHTML
}

const prune = () => {
    try {
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
            const key = window.localStorage.key(i)
            if (!key?.startsWith(PREFIX)) continue
            const snap = JSON.parse(window.localStorage.getItem(key) ?? "null")
            if (!snap || Date.now() - snap.createdAt > MAX_AGE_MS) window.localStorage.removeItem(key)
        }
    } catch {
        // ignore
    }
}

// Opens <current page>/print or /export in a new tab with a snapshot of `content`.
export const openReportView = (
    kind: ReportViewKind,
    { title, module, content, filters, fullExport }: {
        title: string, module: string, content: HTMLElement | null, filters?: HTMLElement | null, fullExport?: FullExport
    },
) => {
    if (!content) return
    prune()
    const id = Math.random().toString(36).slice(2, 10)
    const snapshot: ReportSnapshot = {
        title,
        module,
        filters: readFilterValues(filters ?? null),
        html: cleanHtml(content),
        createdAt: Date.now(),
        hasFullExport: !!fullExport,
    }
    if (fullExport) {
        const w = window as any
        w[FULL_EXPORTS_KEY] = { ...(w[FULL_EXPORTS_KEY] ?? {}), [id]: fullExport }
    }
    try {
        window.localStorage.setItem(PREFIX + id, JSON.stringify(snapshot))
    } catch {
        alert("The report is too large to open in a new tab. Narrow the filters and try again.")
        return
    }
    const base = window.location.pathname.replace(/\/$/, "")
    window.open(`${base}/${kind}?snapshot=${id}`, "_blank")
}

export const readReportSnapshot = (id: string | null): ReportSnapshot | null => {
    if (!id) return null
    try {
        const raw = window.localStorage.getItem(PREFIX + id)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const csvCell = (text: string) => {
    const t = text.replace(/\s+/g, " ").trim()
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t
}

// Every <table> in the snapshot as CSV text (header rows, body rows, footer rows)
export const tablesToCsv = (html: string): string[] => {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html")
    return Array.from(doc.querySelectorAll("table")).map(table =>
        Array.from(table.querySelectorAll("tr"))
            .map(tr => Array.from(tr.querySelectorAll("th, td")).map(c => {
                const cell = csvCell(c.textContent ?? "")
                const span = Number(c.getAttribute("colspan") ?? 1)
                return [cell, ...Array(Math.max(0, span - 1)).fill("")].join(",")
            }).join(","))
            .filter(line => line.replace(/,/g, "").trim() !== "")
            .join("\n")
    )
}

export const downloadText = (fileName: string, text: string, type = "text/csv;charset=utf-8") => {
    const blob = new Blob(["﻿" + text], { type })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}
